import { ChevronLeft, ChevronRight } from "lucide-react";
import { PER_PAGE_OPTIONS } from "@salesboard/contracts";
import { useT } from "@/i18n";
import { formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Select } from "../input";

/**
 * Pagination, ported from bd-admin — the floating pill every list in the agency's apps
 * uses, plus an inline variant for embedded lists.
 *
 * Reusable by design: it takes numbers and callbacks, knows nothing about the entity,
 * and is driven by `useServerTable`. Differences from bd-admin, both deliberate: it
 * centres rather than offsetting by a sidebar (this app has a top bar), and it paints
 * from theme tokens rather than `bg-white/80` so it survives a dark theme.
 */

export type PaginationControlProps = {
  page: number;
  perPage: number;
  /** Count after filters, before pagination — what makes "1–30 of 412" honest. */
  total: number;
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  variant?: "floating" | "inline";
  className?: string;
};

function Controls({
  page,
  perPage,
  total,
  totalPages,
  onPageChange,
  onPerPageChange,
}: PaginationControlProps & { totalPages: number }) {
  const t = useT();
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  return (
    <>
      {/* One phrase, not three fragments joined by an en dash and the word "of": the range
          separator and the preposition are both language-specific. */}
      <span className="tabular text-sm opacity-70">
        {t("pagination.range", formatNumber(from), formatNumber(to), formatNumber(total))}
      </span>

      <div className="flex items-center gap-2">
        {onPerPageChange && (
          <div className="hidden md:block">
            <Select
              aria-label={t("pagination.rowsPerPage")}
              value={String(perPage)}
              onChange={(event) => onPerPageChange(Number(event.target.value))}
              // Two fixes for one empty-looking box. The colours were `currentColor`-derived
              // (`bg-current/10` under `text-current`), which on the pill's translucent surface
              // painted the number in a tint of itself; and the geometry left it nowhere to
              // draw — `Select`'s own `px-3 pr-8` inside `w-16` is under 20px of content box,
              // so the value was clipped behind the native arrow. Explicit tokens, a wider box
              // and padding that accounts for the platform's own chevron.
              className="border-border bg-background text-foreground h-7 w-[4.75rem] pr-7 pl-2 text-xs"
            >
              {PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </div>
        )}

        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t("pagination.previous")}
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-current/20 bg-current/10 transition-colors hover:bg-current/20 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
          <span className="tabular px-2 text-sm opacity-70">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            aria-label={t("pagination.next")}
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-current/20 bg-current/10 transition-colors hover:bg-current/20 disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
    </>
  );
}

export function PaginationControl(props: PaginationControlProps) {
  const t = useT();
  const { page, perPage, total, onPageChange, variant = "floating", className } = props;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  // Nothing to page through — showing the control would be noise.
  if (total <= Math.min(perPage, PER_PAGE_OPTIONS[0])) return null;

  if (variant === "inline") {
    return (
      <div
        className={cn(
          "bg-muted text-foreground flex w-full items-center justify-between gap-4 rounded-b-md px-5 py-2 whitespace-nowrap",
          className,
        )}
      >
        <Controls {...props} totalPages={totalPages} />
      </div>
    );
  }

  return (
    <>
      {/* Mobile: a compact pill — arrows and a counter, nothing else. */}
      <div className="bg-card/85 ring-border fixed bottom-4 left-1/2 z-10 flex h-[52px] -translate-x-1/2 items-center overflow-hidden rounded-full shadow-lg ring-1 backdrop-blur-md md:hidden">
        <button
          type="button"
          aria-label={t("pagination.previous")}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="hover:bg-accent flex h-full w-[52px] items-center justify-center transition-colors disabled:opacity-30"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden />
        </button>
        <span className="tabular min-w-10 px-1 text-center text-sm font-semibold select-none">
          {page}/{totalPages}
        </span>
        <button
          type="button"
          aria-label={t("pagination.next")}
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="hover:bg-accent flex h-full w-[52px] items-center justify-center transition-colors disabled:opacity-30"
        >
          <ChevronRight className="h-5 w-5" aria-hidden />
        </button>
      </div>

      {/* Desktop: the full bar. */}
      <div
        className={cn(
          "bg-card/85 ring-border fixed bottom-4 left-1/2 z-10 hidden w-fit min-w-[420px] -translate-x-1/2 items-center justify-between gap-4 rounded-full px-5 py-2 whitespace-nowrap shadow-2xl ring-1 backdrop-blur-md md:flex",
          className,
        )}
      >
        <Controls {...props} totalPages={totalPages} />
      </div>
    </>
  );
}
